import { EqualsIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { InfoOutlineIcon, MinusIcon, PlusIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  formatCurrency,
  formatPercent,
  roundToPrecision,
} from 'src/utils/numberFormatter';
import { getMarketplaceSettings } from 'src/utils/userUtils';
import {
  BaseListingPriceCalculation,
  BulkListingPriceCalculation,
  Feature,
  ListingPriceCalculation,
  Marketplace,
  Seating,
} from 'src/WebApiController';

import { ComparableListing } from '../../ComparableListings';
import * as styles from './ComparableListingDisplayTooltip.css';
import { DividerLine } from './DividerLine';

export type ComparableListingDisplayTooltipProps = {
  listingId?: number;
  seating?: Seating;
  price?: ReactNode;
  unitCost?: ReactNode;
  listingPriceCalc?: ListingPriceCalculation | null;
  listingPriceCalculations?: BulkListingPriceCalculation[] | null;
  comparableListings?: ComparableListing[] | null;
  includeFanInventory?: boolean;
  officeId?: number | null;
};

const parseCurrencyToNumber = (value: React.ReactNode): number => {
  if (typeof value === 'string') {
    const numericString = value.replace(/[^0-9.-]+/g, '');
    const num = parseFloat(numericString);

    return isNaN(num) ? 0 : num;
  }
  if (typeof value === 'number') {
    return value;
  }

  return 0;
};

export function ComparableListingDisplayTooltip({
  listingId,
  seating,
  price,
  unitCost,
  listingPriceCalc,
  listingPriceCalculations,
  comparableListings,
  includeFanInventory,
  officeId,
}: ComparableListingDisplayTooltipProps) {
  const myListingText = useContent(ContentId.MyListings);
  const myListingTextTrunc = useMemo(() => {
    return myListingText.slice(0, -1);
  }, [myListingText]);

  const { getUiCurrency } = useLocalizationContext();
  const { isDarkMode } = useSiteTheme();

  const hasPricerBreakdownSellerFee = useUserHasFeature(
    Feature.PricerBreakdownSellerFee
  );

  const { loginContext } = useAppContext();
  const stubHubMkpSettings = useMemo(() => {
    return getMarketplaceSettings(
      loginContext?.user?.activeAccount,
      Marketplace.StubHub,
      officeId
    );
  }, [loginContext?.user?.activeAccount, officeId]);

  const listingPriceCalcBase: BaseListingPriceCalculation | null =
    useMemo(() => {
      if (listingPriceCalc) {
        return listingPriceCalc as BaseListingPriceCalculation;
      }

      const foundListingPriceCalc = listingPriceCalculations?.find(
        (x) => x.listingId === listingId
      );

      if (foundListingPriceCalc) {
        return foundListingPriceCalc as BaseListingPriceCalculation;
      }

      return null;
    }, [listingPriceCalc, listingPriceCalculations, listingId]);

  const uiCurrency = useMemo(
    () => getUiCurrency(listingPriceCalcBase?.websitePrice?.currency ?? 'USD'),
    [getUiCurrency, listingPriceCalcBase?.websitePrice?.currency]
  );

  const compPrice = useMemo(() => {
    return listingPriceCalcBase?.udrRefPrc?.disp;
  }, [listingPriceCalcBase?.udrRefPrc?.disp]);

  const brokerInvPremium = useMemo(() => {
    const newPrcInclFanInv = listingPriceCalcBase?.newPrcInclFanInv;
    const websitePrice = listingPriceCalcBase?.websitePrice?.amt;

    if (!newPrcInclFanInv || !websitePrice) {
      return null;
    }
    return ((websitePrice - newPrcInclFanInv) / newPrcInclFanInv) * 100;
  }, [listingPriceCalcBase]);

  const content = (
    <Stack gap="s" justifyContent="center" alignItems="center">
      <span className={styles.responsiveText}>{myListingTextTrunc}</span>
      <InfoOutlineIcon
        withHoverEffect
        size={vars.iconSize.s}
        fill={isDarkMode ? 'black' : vars.color.borderLightActive}
      />
    </Stack>
  );

  const hasRelative = Boolean(
    listingPriceCalcBase?.udrRel || listingPriceCalcBase?.udrRelPrm
  );
  const hasAbsolute = Boolean(
    listingPriceCalcBase?.udrAbs || listingPriceCalcBase?.udrAbsPrm
  );

  const sellerFeeRate = stubHubMkpSettings?.sellerFee ?? 0;
  const showSellerFeeLine = hasPricerBreakdownSellerFee && sellerFeeRate > 0;

  const { sellerFeeAmount, projectedProceeds } = useMemo(() => {
    const priceAsNumber = parseCurrencyToNumber(price);
    const unitCostAsNumber = parseCurrencyToNumber(unitCost);

    if (!hasPricerBreakdownSellerFee) {
      const result = priceAsNumber * (1 - sellerFeeRate) - unitCostAsNumber;
      return {
        sellerFeeAmount: 0,
        projectedProceeds: parseFloat(result.toFixed(2)),
      };
    }

    // Fee is rounded first and subtracted from the price so that
    // price - fee - unit cost adds up exactly to the displayed projected profit
    const feeAmount = parseFloat((priceAsNumber * sellerFeeRate).toFixed(2));
    const result = priceAsNumber - feeAmount - unitCostAsNumber;
    return {
      sellerFeeAmount: feeAmount,
      projectedProceeds: parseFloat(result.toFixed(2)),
    };
  }, [price, unitCost, sellerFeeRate, hasPricerBreakdownSellerFee]);

  return (
    <Tooltip
      triggerContent={content}
      contentStyle={{
        padding: 0,
        minWidth: '13vw',
      }}
      align="center"
    >
      <div
        className={clsx(styles.tooltipContainer, {
          [styles.tooltipContainerDark]: isDarkMode,
        })}
      >
        <Stack
          direction="column"
          className={clsx(styles.sectionContainer, {
            [styles.sectionContainerDark]: isDarkMode,
          })}
        >
          <Stack
            justifyContent="spaceBetween"
            gap="xxl"
            className={styles.section}
          >
            <span>
              <Content id={ContentId.UnitCost} />
            </span>
            {unitCost ?? <Content id={ContentId.NA} />}
          </Stack>
        </Stack>

        <Stack
          direction="column"
          className={clsx(styles.sectionContainer, {
            [styles.sectionContainerDark]: isDarkMode,
          })}
        >
          <Stack
            justifyContent="spaceBetween"
            gap="xxl"
            className={styles.section}
          >
            <span>
              <Content id={ContentId.PricingAgainst} />
            </span>
            {compPrice ?? <Content id={ContentId.NA} />}
          </Stack>
          {brokerInvPremium && brokerInvPremium > 0 && includeFanInventory && (
            <Stack
              justifyContent="spaceBetween"
              gap="xxl"
              className={styles.subText}
            >
              <Stack alignItems="center" gap="s">
                <InfoOutlineIcon
                  size={vars.iconSize.m}
                  fill={vars.color.textDisabled}
                />
                <span>
                  <Content id={ContentId.BrokerInvPrem} />
                </span>
              </Stack>
              <span>{roundToPrecision(brokerInvPremium, 2)}%</span>
            </Stack>
          )}
          {hasRelative && (
            <>
              <DividerLine>
                <MinusIcon
                  size={vars.iconSize.xxs}
                  fill={isDarkMode ? 'black' : 'white'}
                />
              </DividerLine>

              <div className={styles.section}>
                {!!listingPriceCalcBase?.udrRel && (
                  <Stack justifyContent="spaceBetween" gap="xxl">
                    <span>
                      % <Content id={ContentId.DiscountApplied} />
                    </span>
                    {formatPercent(listingPriceCalcBase.udrRel, 0)}
                  </Stack>
                )}
                {!!listingPriceCalcBase?.udrRelPrm && (
                  <Stack justifyContent="spaceBetween" gap="xxl">
                    <span>
                      % <Content id={ContentId.RankPremium} />
                    </span>
                    {formatPercent(listingPriceCalcBase.udrRelPrm, 0)}
                  </Stack>
                )}
              </div>
            </>
          )}
          {hasAbsolute && (
            <>
              <DividerLine>
                <PlusIcon
                  size={vars.iconSize.xxs}
                  fill={isDarkMode ? 'black' : 'white'}
                />
              </DividerLine>

              <div className={styles.section}>
                {!!listingPriceCalcBase?.udrAbs && (
                  <Stack justifyContent="spaceBetween" gap="xxl">
                    <span>
                      {uiCurrency.sym}{' '}
                      <Content id={ContentId.DiscountApplied} />
                    </span>
                    {formatCurrency(
                      listingPriceCalcBase.udrAbs,
                      uiCurrency.code,
                      uiCurrency.dec
                    )}
                  </Stack>
                )}
                {!!listingPriceCalcBase?.udrAbsPrm && (
                  <Stack justifyContent="spaceBetween" gap="xxl">
                    <span>
                      {uiCurrency.sym} <Content id={ContentId.RankPremium} />
                    </span>
                    {formatCurrency(
                      listingPriceCalcBase.udrAbsPrm,
                      uiCurrency.code,
                      uiCurrency.dec
                    )}
                  </Stack>
                )}
              </div>
            </>
          )}
          <DividerLine>
            <div className={styles.equalsIconContainer}>
              <EqualsIcon
                className={styles.equalsIcon}
                stroke={isDarkMode ? 'black' : 'white'}
                strokeWidth={2}
              />
            </div>
          </DividerLine>
          <div className={styles.pricesContainer}>
            <Stack
              justifyContent="spaceBetween"
              gap="xxl"
              className={styles.section}
            >
              <SeatingInfo
                darkBackground={!isDarkMode}
                lightBackground={isDarkMode}
                section={seating?.section ?? ''}
                row={seating?.row ?? ''}
                showSectionPrefix
                // purposely leaving these out
                seatFr={''}
                seatTo={''}
              />
              {price}
            </Stack>

            {showSellerFeeLine && (
              <Stack justifyContent="spaceBetween" className={styles.subText}>
                <span>
                  <Content id={ContentId.SellerFee} /> (
                  {formatPercent(sellerFeeRate, 0, undefined, true)})
                </span>
                {formatCurrency(
                  -sellerFeeAmount,
                  uiCurrency.code,
                  uiCurrency.dec
                )}
              </Stack>
            )}

            <Stack justifyContent="spaceBetween" className={styles.subText}>
              <span>
                <Content id={ContentId.ProjectedProfit} />
              </span>
              {formatCurrency(
                projectedProceeds,
                uiCurrency.code,
                uiCurrency.dec
              )}
            </Stack>
          </div>
        </Stack>
      </div>
    </Tooltip>
  );
}
